import { API } from '../apis/limeAuthUtils/apiWrapper';
import { getCurrentAuthUserEmail } from '../lib/utils';

/**
 * Send a message to the device recovery assistant
 * @param {string} question - The user's recovery question/command
 * @param {string} conversationId - The conversation ID (empty string for new conversations)
 * @returns {Promise<Object>} - Response from the device recovery service
 */
export async function sendDeviceRecoveryMessage(question, conversationId = '') {
    try {
        const userEmail = await getCurrentAuthUserEmail();

        const apiName = 'v1/llm-service';
        const myInit = {
            body: {
                question: question,
                conversation_id: conversationId,
                requester: userEmail,
            },
        };

        const response = await API.post(apiName, '/v1/recover-device', myInit);

        // Handle different response formats from API Gateway/Lambda
        let parsedResponse;

        if (response && typeof response === 'object') {
            // If response has a body field (API Gateway format), parse it
            if (response.body && typeof response.body === 'string') {
                try {
                    parsedResponse = JSON.parse(response.body);
                } catch (parseError) {
                    parsedResponse = response;
                }
            } else if (response.answer) {
                // Direct response format
                parsedResponse = response;
            } else {
                // Fallback: use the response as-is
                parsedResponse = response;
            }
        } else {
            throw new Error('Invalid response format received from server');
        }

        // Validate that we have an answer
        if (!parsedResponse.answer) {
            throw new Error('Invalid response: missing answer field');
        }

        return parsedResponse;
    } catch (error) {
        // Handle different types of errors
        if (error.response) {
            // API returned an error response
            throw new Error(
                `Device recovery assistant is temporarily unavailable (${error.response.status}). Please try again later.`
            );
        } else if (error.code === 'NetworkError' || (error.message && error.message.includes('Network'))) {
            // Network connectivity issues
            throw new Error(
                'Unable to connect to device recovery assistant. Please check your connection and try again.'
            );
        } else if (error.code === 'TimeoutError' || (error.message && error.message.includes('timeout'))) {
            // Request timeout
            throw new Error('Request timed out. Please try again with a shorter message.');
        } else if (error.message && error.message.includes('answer field')) {
            // Response parsing error
            throw new Error('Received invalid response from device recovery assistant. Please try again.');
        } else {
            // Generic error
            throw new Error('Device recovery assistant is temporarily unavailable. Please try again later.');
        }
    }
}

/**
 * Send a chat message to the LLM service
 * @param {string} question - The user's question
 * @param {string} conversationId - The conversation ID (empty string for new conversations)
 * @returns {Promise<Object>} - Response from the LLM service
 */
export async function sendChatMessage(question, conversationId = '') {
    try {
        const userEmail = await getCurrentAuthUserEmail();

        const apiName = 'v1/llm-service';
        const myInit = {
            body: {
                question: question,
                conversation_id: conversationId,
                requester: userEmail,
            },
        };

        const response = await API.post(apiName, '/v1/chat', myInit);

        // Handle different response formats from API Gateway/Lambda
        let parsedResponse;

        if (response && typeof response === 'object') {
            // If response has a body field (API Gateway format), parse it
            if (response.body && typeof response.body === 'string') {
                try {
                    parsedResponse = JSON.parse(response.body);
                } catch (parseError) {
                    parsedResponse = response;
                }
            } else if (response.answer) {
                // Direct response format
                parsedResponse = response;
            } else {
                // Fallback: use the response as-is
                parsedResponse = response;
            }
        } else {
            throw new Error('Invalid response format received from server');
        }

        // Validate that we have an answer
        if (!parsedResponse.answer) {
            throw new Error('Invalid response: missing answer field');
        }

        return parsedResponse;
    } catch (error) {
        // Handle different types of errors
        if (error.response) {
            // API returned an error response
            throw new Error(`Assistant is temporarily unavailable (${error.response.status}). Please try again later.`);
        } else if (error.code === 'NetworkError' || (error.message && error.message.includes('Network'))) {
            // Network connectivity issues
            throw new Error('Unable to connect to assistant. Please check your connection and try again.');
        } else if (error.code === 'TimeoutError' || (error.message && error.message.includes('timeout'))) {
            // Request timeout
            throw new Error('Request timed out. Please try again with a shorter message.');
        } else if (error.message && error.message.includes('answer field')) {
            // Response parsing error
            throw new Error('Received invalid response from assistant. Please try again.');
        } else {
            // Generic error
            throw new Error('Assistant is temporarily unavailable. Please try again later.');
        }
    }
}
